<template>
  <footer v-if="navigationState" class="site-footer noprint">
    <div class="container">
      <div class="footer-grid">
        <!-- Link columns -->
        <nav
          v-for="col in columns"
          :key="col.title"
          class="footer-col"
          :aria-label="col.title"
        >
          <h3 class="footer-heading">{{ col.title }}</h3>
          <ul class="footer-links">
            <li v-for="link in col.links" :key="link.label">
              <!-- Internal page -->
              <router-link v-if="link.to" :to="link.to" class="footer-link">
                <img class="footer-icon" :src="link.icon" alt="" />
                <span>{{ link.label }}</span>
              </router-link>

              <!-- Button-style action (store) -->
              <a v-else-if="link.action" href="#" class="footer-link" @click.prevent="link.action">
                <img class="footer-icon" :src="link.icon" alt="" />
                <span>{{ link.label }}</span>
                <span class="material-icons ext" aria-hidden="true">open_in_new</span>
              </a>

              <!-- External link / download -->
              <a
                v-else
                :href="link.href"
                class="footer-link"
                target="_blank"
                rel="noopener noreferrer"
                :download="link.download || null"
              >
                <img class="footer-icon" :src="link.icon" alt="" />
                <span>{{ link.label }}</span>
                <span v-if="!link.download && !link.mail" class="material-icons ext" aria-hidden="true">open_in_new</span>
              </a>
            </li>
          </ul>
        </nav>

        <!-- Connect -->
        <div class="footer-col">
          <h3 class="footer-heading">Connect</h3>
          <div class="social">
            <a
              v-for="s in social"
              :key="s.label"
              :href="s.href"
              class="social-btn"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="s.label"
              :title="s.label"
            >
              <img :src="s.icon" alt="" />
            </a>
          </div>

        </div>
      </div>
    </div>

    <!-- Bottom bar -->
    <div class="footer-bottom">
      <div class="container footer-bottom-inner">
        <p class="copyright">
          &copy; {{ year }} Spacesaver Corporation. All rights reserved.
        </p>
        <ul class="legal">
          <li v-for="l in legal" :key="l.label">
            <a :href="l.href" target="_blank" rel="noopener noreferrer">{{ l.label }}</a>
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script>
import windowIcon from "@/assets/img/window-line.svg";
import masonryIcon from "@/assets/img/layout-masonry-line.svg";
import profileIcon from "@/assets/img/profile-line.svg";
import fundsIcon from "@/assets/img/funds-box-line.svg";
import numbersIcon from "@/assets/img/numbers-line.svg";
import newspaperIcon from "@/assets/img/newspaper-line.svg";
import swapIcon from "@/assets/img/swap-box-line.svg";
import articleIcon from "@/assets/img/article-line.svg";
import bookIcon from "@/assets/img/book-read-line.svg";
import billIcon from "@/assets/img/bill-line.svg";
import polaroidIcon from "@/assets/img/polaroid-2-line.svg";
import archiveIcon from "@/assets/img/archive-drawer-line.svg";
import honourIcon from "@/assets/img/honour-line.svg";
import questionIcon from "@/assets/img/question-answer-line.svg";
import globalIcon from "@/assets/img/global-line.svg";
import calendarIcon from "@/assets/img/calendar-event-line.svg";
import shieldIcon from "@/assets/img/shield-check-line.svg";
import configuraIcon from "@/assets/img/configura-dashboard.svg";
import feedbackIcon from "@/assets/img/feedback-line.svg";
import storeIcon from "@/assets/img/store-3-line.svg";
import facebookIcon from "@/assets/img/facebook-box-line.svg";
import twitterIcon from "@/assets/img/twitter-line.svg";
import instagramIcon from "@/assets/img/instagram-line.svg";
import linkedinIcon from "@/assets/img/linkedin-box-line.svg";
import youtubeIcon from "@/assets/img/youtube-line.svg";
import pinterestIcon from "@/assets/img/pinterest-line.svg";

export default {
  name: "FooterNew",
  data() {
    return {
      icons: { window: windowIcon, masonry: masonryIcon },
      social: [
        { label: "Facebook", href: "https://www.facebook.com/SpacesaverCorporation", icon: facebookIcon },
        { label: "LinkedIn", href: "https://www.linkedin.com/company/56990/", icon: linkedinIcon },
        { label: "Instagram", href: "https://www.instagram.com/spacesavercorp/", icon: instagramIcon },
        { label: "YouTube", href: "https://www.youtube.com/user/SpacesaverCorp", icon: youtubeIcon },
        { label: "X (Twitter)", href: "https://twitter.com/spacesaver", icon: twitterIcon },
        { label: "Pinterest", href: "https://www.pinterest.com/spacesaver/", icon: pinterestIcon },
      ],
      legal: [
        {
          label: "Order Terms & Conditions",
          href: "https://spacesaver.widen.net/view/pdf/jnqqq9qjk4/spacesaver-terms-and-conditions.pdf?t.download=true&u=sphzbh",
        },
        {
          label: "Statement of Warranty",
          href: "https://spacesaver.widen.net/view/pdf/ookgx9pq3c/spacesaver-statement-of-warranty.pdf?t.download=true&u=sphzbh",
        },
        {
          label: "Return Goods Policy",
          href: "https://spacesaver.widen.net/view/pdf/7qmnznhs7r/spacesaver-return-goods-policy.pdf?t.download=true&u=sphzbh",
        },
      ],
    };
  },
  computed: {
    navigationState() {
      return this.$store.state.nav;
    },
    isDashboard() {
      return this.$store.state.isDashboard;
    },
    hideOrders() {
      return this.$store.getters.hideOrders;
    },
    user() {
      return this.$store.state.user || {};
    },
    year() {
      return new Date().getFullYear();
    },
    columns() {
      // Orders users and non-orders users see a different first column
      const workspace = this.hideOrders
        ? [
            { label: "Home", to: "/home", icon: profileIcon },
            { label: "Announcements", to: "/announcements", icon: articleIcon },
            { label: "From The Fort", to: "/from-the-fort", icon: bookIcon },
            { label: "Trailblazer", to: "/trailblazer", icon: billIcon },
          ]
        : [
            { label: "Home", to: "/home", icon: profileIcon },
            { label: "Order Listings", to: "/listings", icon: fundsIcon },
            { label: "Reports", to: "/reports", icon: numbersIcon },
            { label: "Lead Times", to: "/lead-times", icon: newspaperIcon },
            { label: "IT Projects", to: "/interterritorial", icon: swapIcon },
            { label: "From The Fort", to: "/from-the-fort", icon: bookIcon },
          ];

      return [
        { title: "Workspace", links: workspace },
        {
          title: "Resources",
          links: [
            { label: "Marketing Materials", to: "/marketing-materials", icon: polaroidIcon },
            { label: "Service & Installation", to: "/service-and-installation", icon: archiveIcon },
            { label: "Sales & Training", to: "/sales-and-training", icon: honourIcon },
            { label: "Collaborate", to: "/collaborate", icon: questionIcon },
            { label: "AC Locator", to: "/ac-list", icon: globalIcon },
          ],
        },
        {
          title: "Links",
          links: [
            { label: "Spacesaver.com", href: "https://www.spacesaver.com", icon: windowIcon },
            { label: "Tradeshow Requests", href: "https://tradeshow.spacesaver.com", icon: calendarIcon },
            {
              label: "MSDS",
              href: "https://chemmanagement.ehs.com/9/647ea530-bca3-414b-afb7-3287444d464e/ebinder/?nas=True",
              icon: shieldIcon,
            },
            {
              label: "Configura Download",
              href: "https://spacenet3.s3.amazonaws.com/files/ExtConfigura.zip",
              icon: configuraIcon,
              download: true,
            },
            {
              label: "SpaceNET 3 Feedback",
              href: "mailto:spacenet3feedback@spacesaver.com?subject=SpaceNET%203%20Support",
              icon: feedbackIcon,
              mail: true,
            },
            { label: "Spacesaver Store", action: this.SSCstoreLink, icon: storeIcon },
          ],
        },
      ];
    },
  },
  methods: {
    SSCstoreLink() {
      const params = new URLSearchParams({
        companyGuid: "ae007963-e6dc-47cc-92c5-898fc8add77a",
        email: this.user.Email || "",
        username: this.user.UserName || "",
        fName: this.user.FirstName || "",
        lName: this.user.LastName || "",
      });
      window.open(
        `https://portal.mypropago.com/Security/Spacesaver?${params}`,
        "_blank",
        "noopener"
      );
    },
    setLayout(layout) {
      localStorage.setItem("SSCview", layout);
      this.$store.commit("SET_LAYOUT", layout);
      this.$router.go(0);
    },
  },
};
</script>

<style scoped>
.site-footer {
  background: #fff;
  border-top: 1px solid #e6e8eb;
  color: #575757;
  margin-top: 0;
}

/* ---------- Columns ---------- */
.footer-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 32px;
  padding: 48px 0 36px;
}
.footer-heading {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #4d5967;
  margin: 0 0 14px;
}
.footer-links {
  list-style: none;
  margin: 0;
  padding: 0;
}
.footer-links li {
  margin-bottom: 4px;
}
.footer-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  color: #575757;
  font-size: 15px;
  line-height: 1.3;
  text-decoration: none;
  transition: color 0.2s ease;
}
.footer-link:hover,
.footer-link:focus-visible {
  color: #429da8;
  text-decoration: none;
}
.footer-link:focus-visible,
.social-btn:focus-visible,
.layout-btn:focus-visible,
.legal a:focus-visible {
  outline: 2px solid #429da8;
  outline-offset: 2px;
  border-radius: 2px;
}
.footer-icon {
  width: 18px;
  height: 18px;
  opacity: 0.75;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
}
.footer-link:hover .footer-icon {
  opacity: 1;
}
.ext {
  font-size: 13px;
  color: #b5b5b5;
}

/* ---------- Connect ---------- */
.social {
  display: grid;
  grid-template-columns: repeat(3, 40px);
  gap: 8px;
}
.social-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f1f3f5;
  transition: background 0.2s ease, transform 0.2s ease;
}
.social-btn img {
  width: 20px;
  height: 20px;
  opacity: 0.8;
}
.social-btn:hover {
  background: #e3f1f2;
  transform: translateY(-2px);
}
.social-btn:hover img {
  opacity: 1;
}
.layout-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d6dbe0;
  background: #fff;
  color: #4d5967;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}
.layout-btn img {
  width: 18px;
  height: 18px;
}
.layout-btn:hover {
  border-color: #429da8;
  color: #429da8;
}

/* ---------- Bottom bar ---------- */
.footer-bottom {
  background: #f7f8f9;
  border-top: 1px solid #e6e8eb;
}
.footer-bottom-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-top: 16px;
  padding-bottom: 16px;
}
.copyright {
  margin: 0;
  font-size: 13px;
  color: #737373;
}
.legal {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 20px;
  list-style: none;
  margin: 0;
  padding: 0;
}
.legal a {
  font-size: 13px;
  color: #737373;
  text-decoration: none;
}
.legal a:hover {
  color: #429da8;
  text-decoration: underline;
}

/* ---------- Responsive ---------- */
@media (max-width: 991px) {
  .footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 28px;
    padding: 36px 0 28px;
  }
}
@media (max-width: 575px) {
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .social {
    grid-template-columns: repeat(6, 40px);
  }
  .footer-bottom-inner {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
